import { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import { apiClient } from '../App';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const GROUPS = [
  { value: 'joined', label: 'Joined players' },
  { value: 'waitlisted', label: 'Waitlisted players' },
  { value: 'invited', label: 'Invited players' },
  { value: 'pending_confirmation', label: 'Pending-confirmation players' },
];

const HostPlayerMessageDialog = ({ game, open, onOpenChange, onSent }) => {
  const [groups, setGroups] = useState([]);
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setGroups([]);
    setMessage('');
    setPreview(null);
    setLoadingPreview(false);
    setSending(false);
    setError('');
  }, [game?.id, open]);

  const selectionPayload = useMemo(
    () => (groups.includes('all') ? ['all'] : groups),
    [groups]
  );

  const toggleGroup = (value) => {
    setPreview(null);
    setError('');
    setGroups((current) => {
      if (value === 'all') return current.includes('all') ? [] : ['all'];
      const withoutAll = current.filter((group) => group !== 'all');
      return withoutAll.includes(value)
        ? withoutAll.filter((group) => group !== value)
        : [...withoutAll, value];
    });
  };

  const reviewRecipients = async () => {
    if (!game?.id || selectionPayload.length === 0) return;
    setLoadingPreview(true);
    setError('');
    try {
      const response = await apiClient.post(
        `/games/${game.id}/host/player-message/preview`,
        { groups: selectionPayload }
      );
      setPreview(response.data);
    } catch (requestError) {
      setPreview(null);
      setError(requestError.response?.data?.detail || 'Unable to review recipients.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendMessage = async () => {
    if (!game?.id || !preview || !message.trim()) return;
    setSending(true);
    setError('');
    try {
      const response = await apiClient.post(
        `/games/${game.id}/host/player-message`,
        {
          groups: selectionPayload,
          message: message.trim(),
        }
      );
      onSent?.(response.data);
      onOpenChange(false);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to send the game update.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="host-player-message-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-orange-500" />
            Message players
          </DialogTitle>
          <DialogDescription>
            Send an in-app operational update about {game?.title || 'this session'}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <fieldset>
            <legend className="text-sm font-semibold text-zinc-900">Participation groups</legend>
            <p className="mt-1 text-xs text-zinc-500">
              Select stored participation states. A player in more than one group receives one message.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <label className="flex min-w-0 items-center gap-2 rounded-lg border border-zinc-200 px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={groups.includes('all')}
                  onChange={() => toggleGroup('all')}
                  className="h-4 w-4 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
                />
                All associated players
              </label>
              {GROUPS.map((group) => (
                <label key={group.value} className="flex min-w-0 items-center gap-2 rounded-lg border border-zinc-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={groups.includes(group.value)}
                    onChange={() => toggleGroup(group.value)}
                    className="h-4 w-4 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="min-w-0">{group.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <Button
            type="button"
            variant="outline"
            onClick={reviewRecipients}
            disabled={selectionPayload.length === 0 || loadingPreview}
            className="w-full sm:w-auto"
          >
            {loadingPreview && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Review recipients
          </Button>

          {preview && (
            <div className="rounded-lg bg-zinc-50 px-3 py-3 text-sm" role="status" data-testid="host-player-message-preview">
              <p className="font-medium text-zinc-900">
                {preview.eligible_count} eligible recipient{preview.eligible_count === 1 ? '' : 's'}
              </p>
              <p className="mt-1 text-xs text-zinc-600">
                {preview.excluded_count > 0
                  ? `${preview.excluded_count} selected player${preview.excluded_count === 1 ? '' : 's'} cannot receive this update.`
                  : 'No selected players are excluded.'}
              </p>
              <p className="mt-1 text-xs text-zinc-500">Delivery channel: In-app</p>
            </div>
          )}

          <div>
            <label htmlFor="host-player-message" className="text-sm font-semibold text-zinc-900">
              Message
            </label>
            <Textarea
              id="host-player-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={300}
              rows={5}
              placeholder="Share a factual update about this game."
              className="mt-2 resize-none"
            />
            <p className="mt-1 text-right text-xs text-zinc-500">{message.length}/300</p>
          </div>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-zinc-100 pt-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={sendMessage}
              disabled={!preview || preview.eligible_count < 1 || !message.trim() || sending}
              className="bg-orange-500 text-white hover:bg-orange-600"
            >
              {sending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Send update
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HostPlayerMessageDialog;
